import { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import type { AssetModel } from '../model/types';

export function hasAbout(asset: AssetModel): boolean {
    return (
        asset.summary !== null ||
        asset.characteristics.length > 0 ||
        asset.useCases.length > 0
    );
}

function List({ title, items }: { title: string; items: string[] }) {
    if (items.length === 0) return null;
    return (
        <Box>
            <Typography
                sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: 'text.secondary',
                    mb: 0.25,
                }}
            >
                {title}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {items.map(item => (
                    <Typography
                        key={item}
                        component="li"
                        sx={{ fontSize: '12px', color: 'text.secondary' }}
                    >
                        {item}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}

export function AboutToggle({
    open,
    onToggle,
}: {
    open: boolean;
    onToggle: () => void;
}) {
    return (
        <Typography
            component="span"
            onClick={onToggle}
            sx={{
                fontSize: '12px',
                color: 'text.secondary',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { color: 'text.primary' },
            }}
        >
            {open ? 'Hide about' : 'About (AI-generated)'}
        </Typography>
    );
}

export function AboutBox({
    asset,
    open,
}: {
    asset: AssetModel;
    open: boolean;
}) {
    return (
        <Collapse in={open}>
            <Box
                sx={{
                    mt: 1,
                    maxWidth: 900,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    AI-generated profile.
                </Typography>
                {asset.summary && (
                    <Typography
                        sx={{
                            fontSize: '12px',
                            color: 'text.secondary',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {asset.summary}
                    </Typography>
                )}
                <List
                    title="Key characteristics"
                    items={asset.characteristics}
                />
                <List title="Primary use cases" items={asset.useCases} />
            </Box>
        </Collapse>
    );
}

export function useAboutOpen(): [boolean, () => void] {
    const [open, setOpen] = useState(false);
    return [open, () => setOpen(v => !v)];
}
